import React, { useMemo } from 'react';
import styled from 'styled-components';
import ReactQuill, { Quill } from 'react-quill';
import 'react-quill/dist/quill.snow.css';

// Style-based attributors so font, size and colour are written as inline CSS.
// Class-based output would only render correctly where quill.snow.css is
// loaded; inline CSS survives into read-only views and the keys table.
const FONT_WHITELIST = ['Arial', 'Georgia', 'Tahoma', 'Verdana', 'Helvetica', 'Courier', 'sans-serif', 'serif', 'monospace'];
const SIZE_WHITELIST = ['10px', '12px', '14px', '16px', '18px', '20px', '24px', '32px'];

let quillAttributorsRegistered = false;
const registerQuillAttributors = () => {
  if (quillAttributorsRegistered) return;
  const SizeStyle = Quill.import('attributors/style/size');
  SizeStyle.whitelist = SIZE_WHITELIST;
  Quill.register(SizeStyle, true);

  const FontStyle = Quill.import('attributors/style/font');
  FontStyle.whitelist = FONT_WHITELIST;
  Quill.register(FontStyle, true);

  Quill.register(Quill.import('attributors/style/color'), true);
  Quill.register(Quill.import('attributors/style/background'), true);
  quillAttributorsRegistered = true;
};
registerQuillAttributors();

/** True when the editor holds only markup / whitespace, i.e. nothing to save. */
export const isRichTextEmpty = (html?: string) => {
  if (!html) return true;
  const stripped = html
    .replace(/<(.|\n)*?>/g, '')
    .replace(/&nbsp;/g, '')
    .trim();
  return stripped.length === 0;
};

type Props = {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  hasError?: boolean;
  minHeight?: string;
};

const RichTextEditor = ({ value, onChange, placeholder, hasError, minHeight }: Props) => {
  const modules = useMemo(
    () => ({
      toolbar: [
        [{ font: FONT_WHITELIST }, { size: SIZE_WHITELIST }],
        ['bold', 'italic', 'underline'],
        [{ color: [] }, { background: [] }],
        [{ list: 'ordered' }, { list: 'bullet' }],
        ['clean'],
      ],
    }),
    [],
  );

  const formats = ['font', 'size', 'bold', 'italic', 'underline', 'color', 'background', 'list', 'bullet'];

  return (
    <EditorWrap hasError={hasError} minHeight={minHeight || '120px'}>
      <ReactQuill
        theme="snow"
        value={value || ''}
        onChange={onChange}
        placeholder={placeholder}
        modules={modules}
        formats={formats}
      />
    </EditorWrap>
  );
};

export default RichTextEditor;

const FONT_STACK = `'Segoe UI', -apple-system, BlinkMacSystemFont, 'Helvetica Neue', Arial, Roboto, sans-serif, 'Apple Color Emoji',
    'Segoe UI Emoji', 'Segoe UI Symbol'`;

const fontPickerLabels = FONT_WHITELIST.map(
  font => `
    .ql-snow .ql-picker.ql-font .ql-picker-label[data-value='${font}']::before,
    .ql-snow .ql-picker.ql-font .ql-picker-item[data-value='${font}']::before {
      content: '${font}';
      font-family: ${font.includes(' ') ? `'${font}'` : font};
    }
  `,
).join('');

const sizePickerLabels = SIZE_WHITELIST.map(
  size => `
    .ql-snow .ql-picker.ql-size .ql-picker-label[data-value='${size}']::before,
    .ql-snow .ql-picker.ql-size .ql-picker-item[data-value='${size}']::before {
      content: '${size}';
    }
  `,
).join('');

const EditorWrap = styled.div<{ hasError?: boolean; minHeight: string }>`
  ${fontPickerLabels}
  ${sizePickerLabels}

  .ql-toolbar.ql-snow {
    border: 1px solid ${({ hasError }) => (hasError ? 'var(--danger-500)' : 'var(--border)')};
    border-radius: 8px 8px 0 0;
    background: var(--neutral-50);
    padding: 6px 8px;
  }

  .ql-container.ql-snow {
    border: 1px solid ${({ hasError }) => (hasError ? 'var(--danger-500)' : 'var(--border)')};
    border-top: 0;
    border-radius: 0 0 8px 8px;
    background: var(--neutral-0);
    font-family: ${FONT_STACK};
    font-size: 0.875rem;
  }

  .ql-editor {
    min-height: ${({ minHeight }) => minHeight};
    color: var(--mainText);
    line-height: 1.5;
  }

  .ql-editor.ql-blank::before {
    color: var(--neutral-400);
    font-style: normal;
  }

  .ql-snow .ql-picker.ql-font {
    width: 130px;
  }

  .ql-snow .ql-picker.ql-size {
    width: 80px;
  }
`;
